import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MessageSquare, Send, Lock, Loader2 } from "lucide-react";

// Secure, audit-logged thread between the case owner (client) and the claiming
// attorney. Read-only for admins. Opens only after a case is claimed.
export function MessageThread({ caseId }) {
  const [state, setState] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const load = async (scroll = false) => {
    try {
      const { data } = await api.get(`/cases/${caseId}/messages`);
      setState(data);
      if (scroll) requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 12000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [caseId]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      await api.post(`/cases/${caseId}/messages`, { body });
      setText("");
      await load(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  }

  if (!state?.claimed) {
    return (
      <div className="text-center py-8 px-4" data-testid="messages-locked">
        <div className="h-12 w-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto"><Lock className="w-6 h-6 text-slate-400" /></div>
        <p className="mt-3 text-sm text-slate-500">Secure messaging unlocks once an attorney claims this case.</p>
      </div>
    );
  }

  const messages = state.messages || [];

  return (
    <div className="flex flex-col" data-testid="message-thread">
      <div ref={scrollRef} className="max-h-[420px] overflow-y-auto space-y-3 pr-1">
        {messages.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No messages yet. Start the conversation below.</p>
        ) : messages.map((m) => {
          const mine = m.sender_id === state.self_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.message_id}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-cobalt text-white rounded-br-md" : "bg-slate-100 text-navy rounded-bl-md"}`}>
                {!mine && <div className="text-[11px] font-semibold text-slate-500 mb-0.5">{m.sender_name || (m.sender_role === "attorney" ? "Attorney" : "Client")}</div>}
                <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString()}</div>
              </div>
            </div>
          );
        })}
      </div>

      {state.can_send ? (
        <div className="mt-4 flex items-end gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            data-testid="message-input"
            rows={1}
            placeholder="Write a secure message…"
            className="flex-1 resize-none bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-cobalt/20 focus:border-cobalt outline-none max-h-32"
          />
          <button onClick={send} disabled={sending || !text.trim()} data-testid="message-send-btn"
            className="h-11 w-11 shrink-0 rounded-full bg-cobalt text-white flex items-center justify-center hover:bg-cobalt-hover transition-all active:scale-95 disabled:opacity-40">
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      ) : (
        <p className="mt-4 text-xs text-slate-400 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Read-only oversight view.</p>
      )}
    </div>
  );
}

export function MessagesSection({ caseId }) {
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-cobalt" />
        <h3 className="font-heading font-semibold text-navy">Secure Messages</h3>
      </div>
      <MessageThread caseId={caseId} />
    </div>
  );
}
